import useIsDoctorShell from '@/common/hooks/useIsDoctorShell';
import useIsNewAppShell from '@/common/hooks/useIsNewAppShell';
import { useIsomorphicLayoutEffect } from '@/common/hooks/useIsomorphicLayoutEffect';
import { useKeepAliveActive } from '@/common/hooks/useKeepAliveShown';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { create } from 'zustand';

const BottomNavigation = dynamic(() => import('./bottomNavigation'));

const HIDE_GRACE_MS = 400;

/**
 * New app (utils/newAppShell) and doctors on the mobile site: ONE bottom nav for the whole app, rendered by _app outside the
 * page layouts. Per-page navs remount on every page change and the kept-alive search page keeps
 * its own hidden copy, so the selection pill could never slide reliably between tabs.
 * Layouts only say whether the visible page wants the nav (BottomNavigationSlot).
 */
const useFloatingNavRequests = create<{
  count: number;
  add: (delta: number) => void;
  /** pages that explicitly have no nav (a chat): it goes at once, without the grace below */
  suppressed: number;
  suppress: (delta: number) => void;
}>(set => ({
  count: 0,
  add: delta => set(state => ({ count: state.count + delta })),
  suppressed: 0,
  suppress: delta => set(state => ({ suppressed: state.suppressed + delta })),
}));

/** What a layout renders when its page has no bottom nav: in the new app the nav leaves in the same frame. */
export const BottomNavigationSuppressor = () => {
  const active = useKeepAliveActive();
  const suppress = useFloatingNavRequests(state => state.suppress);
  useIsomorphicLayoutEffect(() => {
    if (!active) return;
    suppress(1);
    return () => suppress(-1);
  }, [active, suppress]);
  return null;
};

/** The floating nav is one persistent instance: in the new app, and for doctors on the mobile site. */
const useUsesFloatingNav = () => {
  const isAppShell = useIsNewAppShell();
  const isDoctorShell = useIsDoctorShell();
  return isAppShell || isDoctorShell;
};

/** What page layouts render: the classic per-page nav on the site, a request in the new app. */
export const BottomNavigationSlot = () => {
  const isFloating = useUsesFloatingNav();
  // false while this page is the hidden kept-alive copy
  const active = useKeepAliveActive();
  const add = useFloatingNavRequests(state => state.add);
  // The server's frame (painted before any JS runs) cannot know the app: it draws both bars and
  // CSS (globals.css, html[data-floating-bottom-nav]) shows the right one, so the new app's pill is
  // there from the first paint instead of a grey docked bar that later turns into it.
  const [resolved, setResolved] = useState(false);

  // layout effects: the hand-over to _app's nav happens before the browser paints — no frame without a bar
  useIsomorphicLayoutEffect(() => setResolved(true), []);
  useIsomorphicLayoutEffect(() => {
    if (!isFloating || !active) return;
    add(1);
    return () => add(-1);
  }, [isFloating, active, add]);

  if (!resolved)
    return (
      <>
        <BottomNavigation variant="docked" />
        <BottomNavigation variant="floating" />
      </>
    );
  return isFloating ? null : <BottomNavigation />;
};

/** Space the floating nav takes at the bottom: 58px pill + 12px gap (bottomNavigation.tsx), + safe area. */
const FLOATING_NAV_HEIGHT_PX = 70;

export const getFloatingNavInset = () => {
  if (typeof document === 'undefined') return FLOATING_NAV_HEIGHT_PX;
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;visibility:hidden;height:env(safe-area-inset-bottom,0px)';
  document.body.appendChild(probe);
  const safeArea = probe.getBoundingClientRect().height;
  probe.remove();
  return FLOATING_NAV_HEIGHT_PX + Math.round(safeArea);
};

/** Rendered once in _app, next to the pages. */
export const FloatingBottomNavigation = () => {
  const isFloating = useUsesFloatingNav();
  const requested = useFloatingNavRequests(state => state.count > 0);
  const suppressed = useFloatingNavRequests(state => state.suppressed > 0);
  const [shown, setShown] = useState(false);

  // A page change can drop the count to 0 for a commit before the next page asks again (e.g. the
  // kept-alive search page mounts separately); hide only if nobody asks within a moment, so the
  // nav is never remounted between two pages that both have it.
  useIsomorphicLayoutEffect(() => {
    if (requested) return setShown(true);
    const timer = setTimeout(() => setShown(false), HIDE_GRACE_MS);
    return () => clearTimeout(timer);
  }, [requested]);

  return isFloating && shown && !suppressed ? <BottomNavigation /> : null;
};
